import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Camera, Plus, Trash2, GitCompare, FileText, FileCode, History, Upload, RotateCcw, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SnapshotsPage = () => {
  const { projectId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [snapshots, setSnapshots] = useState([]);
  const [scriptVersions, setScriptVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newSnap, setNewSnap] = useState({ label: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [diffSnapA, setDiffSnapA] = useState('');
  const [diffSnapB, setDiffSnapB] = useState('');
  const [diffResult, setDiffResult] = useState(null);
  const [showDiff, setShowDiff] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    fetchSnapshots();
    fetchScriptVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchScriptVersions = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/revisions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScriptVersions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch script versions:', error); // non-fatal
    }
  };

  const handleRestoreScript = async (scriptId, versionNumber) => {
    if (!window.confirm(`Restore script version v${versionNumber} as the active version? Newer versions will be archived (not deleted).`)) return;
    setRestoringId(scriptId);
    try {
      await axios.post(`${API}/projects/${projectId}/scripts/${scriptId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Restored v${versionNumber} as active`);
      fetchScriptVersions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoringId(null);
    }
  };

  const fetchSnapshots = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/snapshots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnapshots(response.data);
    } catch (error) {
      toast.error('Failed to load snapshots');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/projects/${projectId}/snapshots`, newSnap, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Version snapshot created');
      setShowCreate(false);
      setNewSnap({ label: '', description: '' });
      fetchSnapshots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create snapshot');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this snapshot? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/snapshots/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Snapshot deleted');
      fetchSnapshots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleCompare = async () => {
    if (!diffSnapA || !diffSnapB || diffSnapA === diffSnapB) {
      toast.error('Select two different snapshots');
      return;
    }
    setComparing(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/snapshots/${diffSnapA}/diff/${diffSnapB}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiffResult(response.data);
      setShowDiff(true);
    } catch (error) {
      toast.error('Failed to compare snapshots');
    } finally {
      setComparing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] p-4 md:p-8" data-testid="snapshots-page">
      <div className="max-w-6xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(`/projects/${projectId}/breakdown`)}
          className="text-[#a0a0a0] hover:text-[#f0f0f0] mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Breakdown
        </Button>

        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-['DM_Serif_Display'] text-3xl md:text-4xl text-[#f0f0f0] mb-2">Version History</h1>
            <p className="text-[#a0a0a0]">Track script revisions and breakdown snapshots</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/projects/${projectId}/upload?new=1`)}
              className="border-[#e8c547]/40 text-[#e8c547] hover:bg-[#e8c547] hover:text-black"
              data-testid="upload-new-script-from-versions-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload New Script
            </Button>
            <Button
              onClick={() => setShowCreate(true)}
              className="bg-[#e8c547] hover:bg-[#d6b539] text-black font-medium"
              data-testid="create-snapshot-btn"
            >
              <Camera className="h-5 w-5 mr-2" />
              Save Breakdown Snapshot
            </Button>
          </div>
        </div>

        {/* ── SCRIPT VERSIONS ── */}
        <div className="mb-8" data-testid="script-versions-section">
          <div className="flex items-center gap-2 mb-4">
            <FileCode className="h-5 w-5 text-[#4a9eff]" />
            <h2 className="font-['DM_Serif_Display'] text-xl text-[#f0f0f0]">Script Versions</h2>
            <Badge className="bg-[#4a9eff]/20 text-[#4a9eff] border-0 text-xs">{scriptVersions.length}</Badge>
          </div>
          {scriptVersions.length === 0 ? (
            <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-6 text-center text-[#a0a0a0] text-sm">
              No script uploaded yet. Click "Upload New Script" above to begin.
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {scriptVersions.map((script) => {
                const isActive = !script.is_archived;
                return (
                  <Card
                    key={script.id}
                    data-testid={`script-version-${script.version_number}`}
                    className={`p-4 border-l-4 transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-br from-[#4a9eff]/10 to-[#1a1a1a] border-l-[#4a9eff] border-t border-r border-b border-[#4a9eff]/30 shadow-lg shadow-[#4a9eff]/10'
                        : 'bg-[#0d0d0d] border-l-[#5a5a5a] border-t border-r border-b border-[#2a2a2a] opacity-80'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                          isActive ? 'bg-[#4a9eff]/20' : 'bg-[#1a1a1a]'
                        }`}>
                          <FileCode className={`h-4 w-4 ${isActive ? 'text-[#4a9eff]' : 'text-[#a0a0a0]'}`} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className={`font-bold text-sm ${isActive ? 'text-[#4a9eff]' : 'text-[#a0a0a0]'}`}>
                              v{script.version_number}
                            </span>
                            {isActive ? (
                              <Badge className="bg-[#4a9eff]/20 text-[#4a9eff] border-0 text-[10px] px-1.5 py-0">
                                <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> ACTIVE
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="border-[#3a3a3a] text-[#a0a0a0] text-[10px] px-1.5 py-0">
                                ARCHIVED
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-[#f0f0f0] font-medium truncate mb-1" title={script.filename}>
                      {script.filename}
                    </div>
                    <div className="text-[11px] text-[#a0a0a0] mb-3">
                      {new Date(script.uploaded_at).toLocaleString()}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <Badge className={`text-[10px] border-0 ${
                        script.processing_status === 'complete'
                          ? 'bg-[#44bb88]/20 text-[#44bb88]'
                          : script.processing_status === 'error'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-[#e8c547]/20 text-[#e8c547]'
                      }`}>
                        {script.processing_status}
                      </Badge>
                      {!isActive && script.processing_status === 'complete' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRestoreScript(script.id, script.version_number)}
                          disabled={restoringId === script.id}
                          className="h-7 px-2 text-xs text-[#e8c547] hover:bg-[#e8c547]/10"
                          data-testid={`restore-script-${script.version_number}`}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" /> {restoringId === script.id ? 'Restoring…' : 'Restore'}
                        </Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* ── BREAKDOWN SNAPSHOTS ── */}
        <div className="flex items-center gap-2 mb-4">
          <History className="h-5 w-5 text-[#e8c547]" />
          <h2 className="font-['DM_Serif_Display'] text-xl text-[#f0f0f0]">Breakdown Snapshots</h2>
          <Badge className="bg-[#e8c547]/20 text-[#e8c547] border-0 text-xs">{snapshots.length}</Badge>
        </div>

        {/* Compare bar */}
        {snapshots.length >= 2 && (
          <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-4 mb-6" data-testid="compare-bar">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-[#a0a0a0] text-sm">Compare:</span>
              <Select value={diffSnapA} onValueChange={setDiffSnapA}>
                <SelectTrigger className="w-48 bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]" data-testid="diff-a-select">
                  <SelectValue placeholder="Version A" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
                  {snapshots.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-[#a0a0a0]">vs</span>
              <Select value={diffSnapB} onValueChange={setDiffSnapB}>
                <SelectTrigger className="w-48 bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]" data-testid="diff-b-select">
                  <SelectValue placeholder="Version B" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
                  {snapshots.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button
                onClick={handleCompare}
                disabled={comparing}
                className="bg-[#4a9eff] hover:bg-[#3a8eef] text-white"
                data-testid="compare-btn"
              >
                <GitCompare className="h-4 w-4 mr-2" />
                {comparing ? 'Comparing...' : 'Compare'}
              </Button>
            </div>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-12 text-[#a0a0a0]">Loading snapshots...</div>
        ) : snapshots.length === 0 ? (
          <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center">
            <Camera className="h-16 w-16 text-[#a0a0a0] mx-auto mb-4" />
            <h3 className="font-['DM_Serif_Display'] text-2xl text-[#f0f0f0] mb-2">No versions yet</h3>
            <p className="text-[#a0a0a0]">Save snapshots to track changes over time</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="snapshots-grid">
            {snapshots.map((snap) => (
              <Card key={snap.id} className="bg-[#1a1a1a] border-[#2a2a2a] p-5" data-testid={`snapshot-${snap.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="h-10 w-10 rounded-lg bg-[#e8c547]/10 flex items-center justify-center">
                    <Camera className="h-5 w-5 text-[#e8c547]" />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(snap.id)}
                    className="text-red-400 hover:bg-red-500/10 h-7 w-7 p-0"
                    data-testid={`delete-snap-${snap.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <h3 className="font-['DM_Serif_Display'] text-lg text-[#f0f0f0] mb-1">{snap.label}</h3>
                {snap.description && <p className="text-[#a0a0a0] text-sm mb-3">{snap.description}</p>}
                <div className="flex flex-wrap gap-2 mb-2">
                  <Badge className="bg-[#4a9eff]/20 text-[#4a9eff] text-xs">{snap.scenes_count} scenes</Badge>
                  <Badge className="bg-[#e8c547]/20 text-[#e8c547] text-xs">{snap.items_count} items</Badge>
                  <Badge variant="outline" className="border-[#2a2a2a] text-[#a0a0a0] text-xs">{snap.characters_count} chars</Badge>
                </div>
                <div className="text-xs text-[#a0a0a0]">
                  by {snap.created_by_name} • {new Date(snap.created_at).toLocaleString()}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
            <DialogHeader>
              <DialogTitle className="font-['DM_Serif_Display'] text-2xl">Save Current Version</DialogTitle>
              <DialogDescription className="text-[#a0a0a0]">
                Take a snapshot of the current breakdown state for future reference
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-[#f0f0f0] mb-2 block">Version Label *</label>
                <Input
                  value={newSnap.label}
                  onChange={(e) => setNewSnap({ ...newSnap, label: e.target.value })}
                  className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0]"
                  placeholder="e.g., Draft v1, Pre-production lock, Director's notes"
                  required
                  data-testid="snap-label-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#f0f0f0] mb-2 block">Description</label>
                <textarea
                  value={newSnap.description}
                  onChange={(e) => setNewSnap({ ...newSnap, description: e.target.value })}
                  className="w-full bg-[#0d0d0d] border border-[#2a2a2a] text-[#f0f0f0] rounded-lg p-3 min-h-[80px]"
                  placeholder="Optional notes about this version..."
                  data-testid="snap-description-input"
                />
              </div>
              <Button
                type="submit"
                disabled={creating}
                className="w-full bg-[#e8c547] hover:bg-[#d6b539] text-black font-medium"
                data-testid="submit-snapshot-btn"
              >
                {creating ? 'Saving...' : 'Save Snapshot'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Diff Dialog */}
        <Dialog open={showDiff} onOpenChange={setShowDiff}>
          <DialogContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['DM_Serif_Display'] text-2xl">Version Comparison</DialogTitle>
              <DialogDescription className="text-[#a0a0a0]">
                {diffResult?.snapshot_a.label} ↔ {diffResult?.snapshot_b.label}
              </DialogDescription>
            </DialogHeader>
            {diffResult && (
              <div className="space-y-6 mt-4" data-testid="diff-result">
                {diffResult.scenes.added.length > 0 && (
                  <div>
                    <h4 className="text-green-400 font-semibold mb-2 flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Added Scenes ({diffResult.scenes.added.length})
                    </h4>
                    <ul className="space-y-1">
                      {diffResult.scenes.added.map(s => (
                        <li key={s.scene_number} className="bg-green-500/10 p-2 rounded text-sm">
                          <span className="text-green-400 font-bold">{formatSceneNumber(s.scene_number, { prefix: '#' })}</span> {s.scene_header}
                          <p className="text-[#a0a0a0] text-xs mt-1">{s.synopsis}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {diffResult.scenes.removed.length > 0 && (
                  <div>
                    <h4 className="text-red-400 font-semibold mb-2 flex items-center gap-2">
                      <Trash2 className="h-4 w-4" />
                      Removed Scenes ({diffResult.scenes.removed.length})
                    </h4>
                    <ul className="space-y-1">
                      {diffResult.scenes.removed.map(s => (
                        <li key={s.scene_number} className="bg-red-500/10 p-2 rounded text-sm">
                          <span className="text-red-400 font-bold">{formatSceneNumber(s.scene_number, { prefix: '#' })}</span> {s.scene_header}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {diffResult.scenes.changed.length > 0 && (
                  <div>
                    <h4 className="text-[#4a9eff] font-semibold mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Changed Scenes ({diffResult.scenes.changed.length})
                    </h4>
                    <ul className="space-y-2">
                      {diffResult.scenes.changed.map(s => (
                        <li key={s.scene_number} className="bg-[#4a9eff]/10 p-3 rounded text-sm">
                          <span className="text-[#4a9eff] font-bold">{formatSceneNumber(s.scene_number, { prefix: '#' })}</span> {s.scene_header}
                          {s.changes && Object.keys(s.changes).length > 0 && (
                            <div className="mt-2 space-y-1 text-xs">
                              {Object.entries(s.changes).map(([key, val]) => (
                                <div key={key}>
                                  <span className="text-[#a0a0a0]">{key}:</span>
                                  <span className="text-red-400 ml-2 line-through">{String(val.before)}</span>
                                  <span className="text-[#a0a0a0] mx-1">→</span>
                                  <span className="text-green-400">{String(val.after)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {s.item_changes && Object.keys(s.item_changes).length > 0 && (
                            <div className="mt-2 pl-2 border-l-2 border-[#4a9eff]/30">
                              <span className="text-xs text-[#a0a0a0] block mb-1">Item Changes:</span>
                              {Object.entries(s.item_changes).map(([cat, diff]) => (
                                <div key={cat} className="text-xs mb-1">
                                  <span className="text-[#e8c547]">{cat.replace(/_/g, ' ')}:</span>
                                  {diff.added.map((d, i) => (
                                    <span key={'a'+i} className="ml-2 text-green-400">+ {d}</span>
                                  ))}
                                  {diff.removed.map((d, i) => (
                                    <span key={'r'+i} className="ml-2 text-red-400 line-through">- {d}</span>
                                  ))}
                                </div>
                              ))}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="bg-[#0d0d0d] p-3 rounded">
                  <span className="text-[#a0a0a0] text-sm">Breakdown items change: </span>
                  <span className={diffResult.items_count_change >= 0 ? 'text-green-400' : 'text-red-400'}>
                    {diffResult.items_count_change >= 0 ? '+' : ''}{diffResult.items_count_change}
                  </span>
                </div>
                {diffResult.scenes.added.length === 0 && diffResult.scenes.removed.length === 0 && diffResult.scenes.changed.length === 0 && (
                  <p className="text-[#a0a0a0] text-center py-8">No scene-level differences between these versions</p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SnapshotsPage;
